import React, { useState, useEffect, useRef } from 'react'
import { CButton, CToast, CToastBody } from '@coreui/react'
import MUIDataTable from 'mui-datatables'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { CSSProperties } from 'react'
import { ShimmerTable } from 'react-shimmer-effects'
import Modal from 'react-modal'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { AiOutlineSecurityScan } from 'react-icons/ai'
import { FaEye } from 'react-icons/fa'
import { MdDeleteOutline } from 'react-icons/md'
import { accessLevel } from 'src/utils'

import PrimaryUrlFilter from './PrimaryUrlFilter'

import ApplicationFilter from './DastApplication'
import { Typography } from '@mui/material'

const QuickScans = () => {
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState('')
  const [filterList, setFilterList] = useState({})
  const [filterOptions, setFilterOptions] = useState({
    applications: [],
    primaryUrls: [],
  })

  const [searchInput, setSearchInput] = useState('')
  const prevSearchInput = useRef(searchInput)

  const [dastScans, setDastScans] = useState([])
  const [onLoading, setOnLoading] = useState(false)

  const [scanToDelete, setScanToDelete] = useState(null)
  const [modalIsOpen, setModalIsOpen] = React.useState(false)

  const [page, setPage] = useState(0)
  const [count, setCount] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [accessLevelRole, setAccessLevelRole] = useState(null)

  const customStyles = {
    content: {
      top: '30%',
      left: '25%',
      width: '50%',
      right: 'auto',
      bottom: 'auto',
      height: '25%',
      backgroundColor: '#ffffff',
      borderRadius: 15,
      borderColor: 'ffffff',
    },
  }

  useEffect(() => {
    if (prevSearchInput.current && searchInput.length === 0) {
      fetchDASTScans(true, 0, rowsPerPage, '', filterList)
    }
    prevSearchInput.current = searchInput
  }, [searchInput])

  const handleClick = (user) => {
    setScanToDelete(user)
    setModalIsOpen(true)
  }

  const handleConfirmation = (confirmed) => {
    if (confirmed) {
      deleteFunction(scanToDelete)
    }
    setModalIsOpen(false)
  }

  const deleteFunction = (scan) => {
    deleteScan(scan)
  }

  const closeModal = async () => {
    setModalIsOpen(false)
  }

  const deleteScan = async (id) => {
    const bearerToken = sessionStorage.getItem('ASIToken')

    try {
      const response = await axios.delete(`api/v1/dast-scans/${id}`, {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      })

      if (response.data.hasOwnProperty('error')) {
        toast.error(response.data.error, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })
      } else {
        toast('Scan deleted', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        })

        fetchDASTScans(true, page, rowsPerPage, searchText, filterList)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const toaster = useRef()
  const exampleToast = (
    <CToast>
      <CToastBody>Success</CToastBody>
    </CToast>
  )

  const override: CSSProperties = {
    display: 'block',
    margin: '0 auto',
    borderColor: 'red',
  }

  const isFirstTime = useRef(true)

  const fetchDASTScans = async (isFirstTime, page, rowsPerPage, searchText, filterList) => {
    if (isFirstTime) {
      setOnLoading(true)
    }

    try {
      const queryParams = new URLSearchParams()

      if (searchText) {
        queryParams.append('search', searchText)
      }

      if (Object.keys(filterList).length > 0) {
        queryParams.append('filterList', JSON.stringify(filterList))
      }

      const queryString = queryParams.toString()
      const url = `/api/v1/dast-scans/all/${page}/${rowsPerPage}${
        queryString ? `?${queryString}` : ''
      }`

      const token = sessionStorage.getItem('ASIToken')
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      })

      setDastScans(response.data.dastScans)
      setCount(response.data.totalCount)
      setFilterOptions(response.data.filterOptions)
    } catch (error) {
      console.error('Error fetching DAST scans:', error)
    } finally {
      setOnLoading(false)
    }
  }

  const latestParams = useRef({
    page: 0,
    rowsPerPage,
    searchText: '',
    filterList: {},
  })

  // Keep ref updated with the latest values of the parameters
  useEffect(() => {
    latestParams.current = { page, rowsPerPage, searchText, filterList }
  }, [page, rowsPerPage, searchText, filterList])

  useEffect(() => {
    // Function to fetch using latest values from the ref
    const fetchUsingLatestParams = () => {
      const { page, rowsPerPage, searchText, filterList } = latestParams.current
      console.log('Fetching with params:', { page, rowsPerPage, searchText, filterList })
      fetchDASTScans(false, page, rowsPerPage, searchText, filterList)
    }

    // Set interval to call fetchUsingLatestParams every 40 seconds
    const interval = setInterval(() => {
      fetchUsingLatestParams()
    }, 10000)

    // Initial fetch
    fetchDASTScans(true, 0, rowsPerPage, '', {})
    isFirstTime.current = false

    return () => clearInterval(interval) // Cleanup interval on unmount
  }, []) // Empty dependency array

  const [user, setUser] = useState({})

  useEffect(() => {
    getUserDetails()
    const role = accessLevel()
    setAccessLevelRole(role)
  }, [])

  const getUserDetails = () => {
    setUser(JSON.parse(sessionStorage.getItem('ASIUser')))
  }

  const goToViewReport = async (scanId) => {
    navigate('/dast-scan-detail?scanId=' + scanId)
  }

  const columns = [
    {
      name: '#',
      label: '#',
      options: {
        sort: true,
        filter: false,
        sortDirection: 'desc',
      },
    },
    {
      label: 'Scan Name',
      options: {
        filter: false,
        download: true,
      },
    },
    // {
    //   label: 'Primary URL',
    //   options: {
    //     filter: true,
    //     download: true,
    //     filterOptions: {
    //       names: filterOptions.primryUrls,
    //       logic: (value, filters) => {
    //         if (filters.length === 0) return false
    //         return !filters.includes(value)
    //       },
    //     },
    //     filterList: filterList['Primary URL'] || [],
    //   },
    // },
    // {
    //   label: 'Application',
    //   options: {
    //     filter: true,
    //     download: true,
    //     filterOptions: {
    //       names: filterOptions.applications,
    //       logic: (value, filters) => {
    //         if (filters.length === 0) return false
    //         return !filters.includes(value)
    //       },
    //     },
    //     filterList: filterList['Application'] || [],
    //   },
    // },

    {
      name: 'primaryurl',
      label: 'Primary URL',
      options: {
        filter: true,
        filterType: 'custom',
        filterList: filterList['Primary URL'] || [],
        filterOptions: {
          names: filterOptions.primaryUrls,
          logic: (value, selected) => {
            if (!selected || selected.length === 0) return false
            return !selected.includes(value)
          },
          display: (filterList, onChange, index, column) => (
            <PrimaryUrlFilter
              filterList={filterList[index] || []}
              onChange={onChange}
              index={index}
              column={column}
              filterOptions={filterOptions}
            />
          ),
        },
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
      },
    },

    {
      name: 'application',
      label: 'Application',
      options: {
        filter: true,
        filterType: 'custom',
        filterList: filterList['Application'] || [],
        filterOptions: {
          names: filterOptions.applications,
          logic: (value, selected) => {
            if (!selected || selected.length === 0) return false
            return !selected.includes(value)
          },
          display: (filterList, onChange, index, column) => (
            <ApplicationFilter
              filterList={filterList[index] || []}
              onChange={onChange}
              index={index}
              column={column}
              filterOptions={filterOptions}
            />
          ),
        },
        setCellProps: () => ({ style: { textAlign: 'center' } }),
        setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
      },
    },
    {
      label: 'URLs Scanned',
      options: {
        filter: false,
        download: true,
      },
    },
    {
      label: 'Issue Count',
      options: {
        filter: false,
        download: true,
      },
    },
    {
      label: 'Started At',
      options: {
        filter: false,
        download: true,
      },
    },
    {
      label: 'Completed At',
      options: {
        filter: false,
        download: true,
      },
    },
    {
      label: 'Status',
      options: {
        filter: false,
        download: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          let theStatus
          let minutesElapsed

          // if (value.lastAPICallFromAppAt) {

          if (
            (value.status == 'In Progress' || value.status == 'Processing') &&
            value.lastAPICallFromAppAt
          ) {
            const lastAPICallTime = new Date(value.lastAPICallFromAppAt) // Convert to Date object
            const currentTime = new Date() // Get the current time (in UTC)

            // Check if lastAPICallTime is valid
            if (!isNaN(lastAPICallTime)) {
              // Calculate the difference in milliseconds
              const timeDifference = currentTime - lastAPICallTime

              // Convert the difference to minutes
              minutesElapsed = Math.floor(timeDifference / 60000) // 60000 ms in a minute

              console.log('Last API call at:', lastAPICallTime.toUTCString())
              console.log('Current time:', currentTime.toUTCString())
              console.log('Time elapsed (minutes):', minutesElapsed)
            } else {
              console.log('Invalid date for lastAPICallFromAppAt')
            }

            if (minutesElapsed > 60) {
              theStatus = 'Aborted'
            } else {
              theStatus = value.status
            }
          } else {
            console.log('lastAPICallFromAppAt is not defined or invalid.')

            theStatus = value.status
          }

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {theStatus && theStatus == 'Completed' ? (
                <span
                  style={{
                    fontSize: 12,
                    color: '#fff',
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: '#195905', // Green background for Completed
                    padding: 5,
                    paddingTop: 10,
                    borderRadius: 5,
                    width: 110,
                  }}
                >
                  {theStatus && theStatus.toUpperCase()}
                </span>
              ) : theStatus == 'Failed' ? (
                <span
                  style={{
                    fontSize: 12,
                    color: '#fff', // White text for Failed
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: '#ff0000', // Red background for Failed
                    padding: 5,
                    paddingTop: 10,
                    borderRadius: 5,
                    width: 110,
                  }}
                >
                  {theStatus && theStatus.toUpperCase()}
                </span>
              ) : theStatus == 'Cancelled' ? (
                <span
                  style={{
                    fontSize: 12,
                    color: '#000', // White text for Failed
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: '#FF8C00', // Red background for Failed
                    padding: 5,
                    paddingTop: 10,
                    borderRadius: 5,
                    width: 110,
                  }}
                >
                  {theStatus && theStatus.toUpperCase()}
                </span>
              ) : theStatus == 'Aborted' ? (
                <span
                  style={{
                    fontSize: 12,
                    color: '#fff', // White text for
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: 'red', // Red background for Aborted
                    padding: 5,
                    paddingTop: 10,
                    borderRadius: 5,
                    width: 110,
                  }}
                >
                  {theStatus && theStatus.toUpperCase()}
                </span>
              ) : (
                <span
                  style={{
                    fontSize: 12,
                    color: '#000', // Black text for other statuses
                    fontWeight: 'normal',
                    textAlign: 'center',
                    backgroundColor: '#ffbf00', // Yellow background for others
                    padding: 5,
                    paddingTop: 10,
                    borderRadius: 5,
                    width: 110,
                  }}
                >
                  {theStatus && theStatus.toUpperCase()}
                </span>
              )}
            </div>
          )
        },
      },
    },
    {
      label: 'View',
      options: {
        filter: false,
        download: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CButton
                color="primary"
                variant="outline"
                onClick={() => goToViewReport(value._id)}
                className="m-2"
                style={{
                  width: '100%',
                  fontSize: 12,
                  fontWeight: 'bold',
                  color: '#fff',
                  borderColor: '#fff',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                View Report
              </CButton>
            </div>
          )
        },
      },
    },
    {
      label: 'Actions',
      options: {
        filter: false,
        download: false,
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {(accessLevelRole == 'Administrator' || accessLevelRole == 'Application Manager') && (
                <CButton
                  color="danger"
                  onClick={() => handleClick(value)}
                  variant="outline"
                  className="m-1"
                  style={{
                    width: '100%',
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: 'red',
                    borderColor: 'red',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MdDeleteOutline style={{ color: 'red', marginRight: 5 }} size={15} />
                  Delete
                </CButton>
              )}
            </div>
          )
        },
      },
    },
  ]

  const getMuiTheme = () =>
    createTheme({
      components: {
        MUIDataTableBodyCell: {
          styleOverrides: {
            root: {
              textAlign: 'left',
              '&:nth-child(1)': {
                width: 30,
              },
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              textAlign: 'left',
              '&:nth-child(1)': {
                width: 30,
              },
            },
          },
        },
      },
    })

  const options = {
    filterType: 'multiselect',
    responsive: 'stacked',
    elevation: 0,
    filter: true,
    download: true,
    print: false,
    search: true,
    searchOpen: true,
    viewColumns: true,
    selectableRows: false,
    downloadOptions: {
      filename: 'DAST_Scans.csv',
      separator: ',',
    },
    rowsPerPageOptions: [10, 20, 60, 100, 150],
    customSearchRender: (searchText, handleSearch, hideSearch, options) => {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '8px 16px',
          }}
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setSearchText(searchInput)
                setPage(0)
                fetchDASTScans(true, 0, rowsPerPage, searchInput, filterList)
              }
            }}
            placeholder="Search scans..."
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              width: '300px',
              fontSize: '14px',
              color: '#000',
            }}
          />
          <button
            onClick={() => {
              setSearchText(searchInput)
              setPage(0)
              fetchDASTScans(true, 0, rowsPerPage, searchInput, filterList)
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Search
          </button>
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('')
                setSearchText('')
                setPage(0)
                fetchDASTScans(true, 0, rowsPerPage, '', filterList)
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: '#d32f2f',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Clear
            </button>
          )}
        </div>
      )
    },
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          setPage(tableState.page)
          fetchDASTScans(true, tableState.page, tableState.rowsPerPage, searchText, filterList)
          break

        case 'changeRowsPerPage':
          setRowsPerPage(tableState.rowsPerPage)
          setPage(0)
          fetchDASTScans(true, 0, tableState.rowsPerPage, searchText, filterList)
          break

        case 'filterChange':
          const newFilterList = {}
          tableState.filterList.forEach((filter, index) => {
            if (filter && filter.length) {
              newFilterList[columns[index].label] = filter
            }
          })
          setFilterList(newFilterList)
          setPage(0)
          fetchDASTScans(true, 0, tableState.rowsPerPage, searchText, newFilterList)
          break

        case 'resetFilters':
          setFilterList({})
          setPage(0)
          fetchDASTScans(true, 0, tableState.rowsPerPage, searchText, {})
          break
      }
    },
    textLabels: {
      body: {
        noMatch: onLoading ? 'Loading data...' : 'No scans created yet',
      },
    },
    serverSide: true,
    count: count,
    page: page,
    rowsPerPage: rowsPerPage,
  }

  var tableData = []

  for (var i = 0; i < dastScans.length; i++) {
    var dataItem = []

    dataItem.push(page * 10 + (i + 1))
    dataItem.push(dastScans[i].scanName)
    dataItem.push(dastScans[i].mainURL)
    dataItem.push(dastScans[i].application.name)

    dataItem.push(dastScans[i].urlsScanned)
    dataItem.push(dastScans[i].issuesCount)

    dataItem.push(
      new Date(dastScans[i].createdAt).toLocaleDateString('en-US') +
        '\n' +
        new Date(dastScans[i].createdAt).toLocaleTimeString('en-US'),
    )

    if (dastScans[i].scanCompletedAt) {
      dataItem.push(
        new Date(dastScans[i].scanCompletedAt).toLocaleDateString('en-US') +
          '\n' +
          new Date(dastScans[i].scanCompletedAt).toLocaleTimeString('en-US'),
      )
    } else {
      dataItem.push('---')
    }

    dataItem.push(dastScans[i]) // for status
    dataItem.push(dastScans[i]) // for view report link
    dataItem.push(dastScans[i]._id) // for delete

    tableData.push(dataItem)
  }

  const goToStartQuickScan = (e) => {
    e.preventDefault()
    navigate('/start-dast-scan')
  }

  const downloadFile = (fileName) => {
    const link = document.createElement('a')
    link.href = `/${fileName}`
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  return (
    <div className="activeScans">
      {setModalIsOpen && (
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          style={customStyles}
          contentLabel=""
        >
          <text style={{ color: '#000', fontSize: 18 }}>
            Are you sure you want to permanently delete this scan?
          </text>
          <br />
          <br />
          <button
            onClick={() => handleConfirmation(true)}
            style={{
              width: 100,
              borderWidth: 0,
              backgroundColor: 'green',
              color: 'white',
              padding: 10,
            }}
          >
            Yes
          </button>
          <button
            onClick={() => handleConfirmation(false)}
            style={{
              marginLeft: 30,
              borderWidth: 0,
              width: 100,
              backgroundColor: 'red',
              color: 'white',
              padding: 10,
            }}
          >
            No
          </button>
        </Modal>
      )}

      <div style={{ width: '100%' }}>
        <div>
          <div
            style={{
              marginBottom: '2rem',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <h4>
              <strong>DAST Scans</strong>
            </h4>

            <p>
              To start a DAST scan, please download our desktop application, available for Linux,
              Windows and Mac OS.
            </p>

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => downloadFile('DAST-Tool-Setup.exe')}
                className="px-3 rounded-2xl shadow-md hover:bg-blue-700"
              >
                💻 Download for Windows
              </button>

              <button
                onClick={() => downloadFile('dast-installer.deb')}
                className="px-3 rounded-xl shadow-md hover:bg-green-700"
              >
                🐧 Download for Linux
              </button>
            </div>

            <CButton
              style={{
                width: 200,
                marginBottom: '2%',
                borderWidth: 0,
                fontSize: 13,
                display: 'none',
                background: '#e50202',
              }}
              onClick={goToStartQuickScan}
              color="primary"
              className="px-3"
            >
              <AiOutlineSecurityScan size={25} color="white" />
              <span style={{ marginLeft: 10 }}>Start a DAST Scan</span>
            </CButton>
          </div>

          {onLoading && <ShimmerTable row={8} col={10} />}

          {!onLoading && (
            <>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  style={{ height: '57vh' }}
                  data={tableData}
                  //  columns={columns}
                  columns={columns.map((col) => ({
                    ...col,
                    options: {
                      ...col.options,
                      customHeadRender: (columnMeta, updateColumnMeta) => (
                        <th
                          style={{
                            backgroundColor: 'lightgray',
                            padding: '10px',
                            textAlign: 'center',
                          }}
                        >
                          {columnMeta.label}
                        </th>
                      ),
                    },
                  }))}
                  options={options}
                />
              </ThemeProvider>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuickScans
